// @ts-nocheck
import {
  MDXEditor,
  UndoRedo,
  BoldItalicUnderlineToggles,
  ListsToggle,
  toolbarPlugin,
  listsPlugin,
  BlockTypeSelect,
  headingsPlugin,
  tablePlugin,
  InsertTable,
  thematicBreakPlugin,
  InsertThematicBreak,
  linkPlugin,
  linkDialogPlugin,
  CreateLink,
  diffSourcePlugin,
  DiffSourceToggleWrapper,
  imagePlugin,
  codeBlockPlugin,
  InsertCodeBlock,
  ChangeCodeMirrorLanguage,
  ConditionalContents,
  codeMirrorPlugin,
} from '@mdxeditor/editor';
// eslint-disable-next-line import/no-unresolved
import '@mdxeditor/editor/style.css';
import cloneDeep from 'lodash/cloneDeep';
import CircularProgress from '@mui/material/CircularProgress';
import {
  Box,
  Checkbox,
  IconButton,
  Popover,
  Typography,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  useTheme,
} from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { ReactComponent as SaveIcon } from '../../../../assets/Icons/lucideIcons/save.svg';
import { ReactComponent as SaveAsIcon } from '../../../../assets/Icons/lucideIcons/save-pen.svg';
import { ReactComponent as KeyboardArrowDownIcon } from '../../../../assets/Icons/lucideIcons/chevron-down.svg';
import { ReactComponent as KeyboardArrowUpIcon } from '../../../../assets/Icons/lucideIcons/chevron-up.svg';
import { ReactComponent as CloseIcon } from '../../../../assets/Icons/lucideIcons/x.svg';
import PropTypes from 'prop-types';

import SwalToast from '../../../../components/Common/swalToast';
import { isValueEmpty } from '../../../../utils/Utils';
import SearchTextField from '../../../../components/Common/SearchTextField';
import {
  EMPTY_ARRAY,
  NOT_AUTHORIZED_TO_ACCESS_MESSAGE,
  NOOP,
} from '../../../../const/CommonConst';
import DocumentLinkedEntities from '../../../../components/Common/Forms/DocumentLinkedEntities';

function sanitizeMarkdown(md) {
  const MAX = 500;
  const REF_LINK_RE = new RegExp(
    String.raw`\[([^\]\r\n]{1,${MAX}})\]\[([^\]\r\n]{0,${MAX}})\]`,
    'g'
  );
  // 1. Replace all [text][ref] or [text][] with [text](#)
  let cleaned = md?.replace(REF_LINK_RE, '[$1](#)');

  // 2. Remove all refere?nce definitions like [ref]: something
  cleaned = cleaned.replace(/^\[[^\]]+\]:\s?.*$/gm, '');

  // 3. Trim to clean whitespace
  return cleaned.trim();
}
const stopPropagation = (e) => e?.stopPropagation();

const START_HERE = 'Start here...';

const renderChangeCodeMirrorLanguage = () => <ChangeCodeMirrorLanguage />;
const renderInsertCodeBlock = () => <InsertCodeBlock />;

const VersionCheckbox = ({
  version,
  selectedVersions,
  handleActionCheckboxChange,
}) => {
  const isChecked = selectedVersions?.find(
    (selectedVersion) => selectedVersion?.version === version?.version
  );
  const isDisabled = selectedVersions?.length >= 2 && !isChecked;

  const onChange = useCallback(
    (event) => {
      handleActionCheckboxChange(event, version);
    },
    [handleActionCheckboxChange, version]
  );

  return (
    <Box
      key={version?.version}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
      }}
    >
      <Checkbox
        checked={!!isChecked}
        disabled={isDisabled}
        onChange={onChange}
        onClick={stopPropagation}
        sx={{
          height: '24px',
          width: '24px',
        }}
        slotProps={{
          input: { 'aria-label': 'controlled' },
        }}
      />
      <Typography variant="body2">Version {version?.version}</Typography>
    </Box>
  );
};
VersionCheckbox.propTypes = {
  version: PropTypes.oneOfType([PropTypes.object]).isRequired,
  selectedVersions: PropTypes.oneOfType([PropTypes.array]).isRequired,
  handleActionCheckboxChange: PropTypes.func.isRequired,
};

const FilterChipVersion = ({
  width = '100px',
  label = 'Version',
  placeholder,
  versionsList = EMPTY_ARRAY,
  getSelectedVersions,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [versions, setVersions] = useState(versionsList || []);
  const [selectedVersions, setSelectedVersions] = useState([]);
  const [versionSearchTerm, setVersionSearchTerm] = useState('');

  const open = Boolean(anchorEl);
  const id = open ? 'version-filter-popover' : undefined;

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, [setAnchorEl]);

  const handleAddCheckedAction = (version) => {
    setSelectedVersions((prev) => {
      const newSelectedVersions = [...prev, version];
      getSelectedVersions(newSelectedVersions);
      return newSelectedVersions;
    });
  };

  const handleRemoveUnCheckedAction = (version) => {
    setSelectedVersions((prev) => {
      const prevClone = cloneDeep(prev);
      const indexToRemove = prevClone?.findIndex(
        (prevVersion) => prevVersion?.version === version?.version
      );
      prevClone?.splice(indexToRemove, 1);
      getSelectedVersions(prevClone);
      return prevClone;
    });
  };

  const handleActionCheckboxChange = useCallback(
    (event, version) => {
      if (event?.target?.checked) {
        handleAddCheckedAction(version);
      } else {
        handleRemoveUnCheckedAction(version);
      }
    },
    [handleAddCheckedAction, handleRemoveUnCheckedAction]
  );

  const handleClick = useCallback(
    (event) => {
      setAnchorEl(event?.currentTarget);
    },
    [setAnchorEl]
  );

  const handleVersionsSearchChange = useCallback(
    (event) => {
      setVersionSearchTerm(event?.target?.value);
    },
    [setVersionSearchTerm]
  );

  useEffect(() => {
    if (versionSearchTerm) {
      const filteredVersions = versions?.filter((version) =>
        `version ${version?.version}`
          ?.toLowerCase()
          ?.includes(versionSearchTerm?.toLowerCase())
      );
      setVersions(filteredVersions);
    } else {
      setVersions(versionsList);
    }
  }, [versionSearchTerm, versionsList]);

  return (
    <Box
      className="typeFilterChip"
      sx={{
        width: { width },
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
        px: '5px',
        py: '14px',
        borderRadius: '4px',
      }}
      onClick={handleClick}
    >
      <Typography variant="body1">{label}</Typography>
      <Box
        sx={{
          display: 'flex',
          gap: 0.5,
        }}
      >
        {selectedVersions?.length > 0 && (
          <Box
            sx={{
              backgroundColor: (theme) => theme?.palette?.primary?.light,
              width: '18px',
              height: '18px',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography
              variant="body3"
              sx={{
                color: 'other.white',
              }}
            >
              {selectedVersions?.length}
            </Typography>
          </Box>
        )}
        {open ? (
          <KeyboardArrowUpIcon
            style={{
              height: '18px',
              width: '18px',
            }}
          />
        ) : (
          <KeyboardArrowDownIcon
            style={{
              height: '18px',
              width: '18px',
            }}
          />
        )}
      </Box>

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        onClick={stopPropagation}
      >
        <Box
          sx={{
            width: '246px',
            maxHeight: '315px',
            overflow: 'auto',
            py: 2,
            px: '10px',
          }}
        >
          <Box
            sx={{
              mb: 2,
            }}
          >
            <SearchTextField
              value={versionSearchTerm}
              onChange={handleVersionsSearchChange}
              placeholder={placeholder}
            />
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            {versions?.map((version) => (
              <VersionCheckbox
                key={version?.version}
                handleActionCheckboxChange={handleActionCheckboxChange}
                version={version}
                selectedVersions={selectedVersions}
              />
            ))}
          </Box>
        </Box>
      </Popover>
    </Box>
  );
};
FilterChipVersion.propTypes = {
  width: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  placeholder: PropTypes.string.isRequired,
  versionsList: PropTypes.oneOfType([PropTypes.array]).isRequired,
  getSelectedVersions: PropTypes.func.isRequired,
};

const ALLOWED_CHUNK_SIZE = 50_000;

const DocumentEditor = ({
  docDetails,
  getDocumentMarkdown,
  getVersionDetails,
  updateDocument,
  saveAsNewVersion,
  mode = 'edit',
  fetchDocumentsList = NOOP,
  entityId,
  isUserAuthorizedForEditDocument,
}) => {
  const ref = useRef(null);
  const [markdown, setMarkdown] = useState('');
  const [versionList, setVersionList] = useState([]);
  const [diffMarkdown, setDiffMarkdown] = useState('__');
  const [viewMode, setViewMode] = useState('rich-text');
  const [isLoading, setIsLoading] = useState(false);
  const [isLargeFile, setIsLargeFile] = useState(false);
  const [linkedEntitiesModalOpen, setLinkedEntitiesModalOpen] = useState(false);
  const [saveAsModalAction, setSaveAsModalAction] = useState(false);
  const { themeMode } = useSelector((state) => state.users);
  const theme = useTheme();

  const fetchVersionList = async (doc) => {
    const versions = await getVersionDetails({
      params: {
        entity_id: doc?.entity_id,
        entity_type: doc?.entity_type,
        document_id: doc?.document_id,
      },
    });
    return versions?.data;
  };

  const fetchDocumentContent = (doc) => {
    setIsLoading(true);
    getDocumentMarkdown({
      params: {
        document_id: doc?.document_id,
      },
    })
      .then((res) => {
        const isExcelFile = doc?.file_type === 'xlsx';
        const fileContent = res?.data || '';
        // In case of excel files, apply size limit to prevent MDX Editor from breaking
        // Excel file support has been removed from the platform. This is here for excel files already in the system
        if (isExcelFile) {
          const allowedChunk = fileContent?.slice(0, ALLOWED_CHUNK_SIZE);
          if (fileContent?.length > ALLOWED_CHUNK_SIZE) {
            setIsLargeFile(true);
          }
          setMarkdown(allowedChunk);
        } else {
          setMarkdown(res?.data);
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const fetchDocumentContentForVersionOne = (doc, commitId) => {
    getDocumentMarkdown({
      params: {
        document_id: doc?.document_id,
        ...(commitId ? { commit_id: commitId } : {}),
      },
    }).then((res) => {
      const data = res?.data || '';
      if (isValueEmpty(data)) {
        setMarkdown(START_HERE);
      } else {
        setMarkdown(res?.data);
      }
    });
  };
  const fetchDocumentContentForVersionTwo = (doc, commitId) => {
    getDocumentMarkdown({
      params: {
        document_id: doc?.document_id,
        ...(commitId ? { commit_id: commitId } : {}),
      },
    }).then((res) => {
      const data = res?.data || '';
      if (isValueEmpty(data)) {
        setDiffMarkdown(START_HERE);
      } else {
        setDiffMarkdown(res?.data);
      }
    });
  };

  const saveDocumentAsNewVersion = () => {
    const body = {
      document_id: docDetails?.document_id,
    };
    saveAsNewVersion(body).then((res) => {
      if (res) {
        SwalToast({
          icon: 'success',
          title: res?.data?.msg,
        });
        fetchVersionList(docDetails).then((versions) => {
          setVersionList(versions);
        });
        fetchDocumentsList({
          entityId,
        });
      }
    });
  };

  useEffect(() => {
    fetchDocumentContent(docDetails);
    fetchVersionList(docDetails).then((versions) => {
      setVersionList(versions);
    });
  }, [docDetails]);

  const handleChange = useCallback(
    (value) => {
      if (!isValueEmpty(value)) {
        setMarkdown(value);
      } else {
        setMarkdown(START_HERE);
      }
    },
    [setMarkdown, isValueEmpty]
  );

  const imageUploadHandler = async (image) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(image);

      reader.onloadend = () => {
        const base64Data = reader?.result?.split(',')[1];
        const dataURI = `data:${image?.type};base64,${base64Data}`;
        resolve(dataURI);
      };

      reader.onerror = () => {
        reject(new Error('Failed to read the file as base64'));
      };
    });
  };

  const handleSaveDocument = (saveAs) => {
    updateDocument({
      entity_id: docDetails?.entity_id,
      entity_type: docDetails?.entity_type,
      document_name: docDetails?.document_name,
      document_id: docDetails?.document_id,
      document_author: docDetails?.document_author,
      document_description: docDetails?.document_description,
      file_content: markdown,
    }).then((res) => {
      if (saveAs) {
        saveDocumentAsNewVersion();
        return;
      }
      if (res) {
        SwalToast({
          icon: 'success',
          title: 'Document saved successfully.',
        });
      }
    });
  };

  const handleVersionsChange = useCallback(
    (versions) => {
      if (versions?.length === 2) {
        fetchDocumentContentForVersionOne(docDetails, versions[0].commit_id);
        fetchDocumentContentForVersionTwo(docDetails, versions[1].commit_id);

        setViewMode('diff');
      } else {
        setViewMode('rich-text');
      }
    },
    [
      fetchDocumentContentForVersionOne,
      fetchDocumentContentForVersionTwo,
      docDetails,
      setViewMode,
    ]
  );

  const handleSaveClick = useCallback(() => {
    setSaveAsModalAction(false);
    setLinkedEntitiesModalOpen(true);
  }, [setLinkedEntitiesModalOpen, setSaveAsModalAction]);
  const handleSaveAsClick = useCallback(() => {
    setSaveAsModalAction(true);
    setLinkedEntitiesModalOpen(true);
  }, [setLinkedEntitiesModalOpen, setSaveAsModalAction]);

  const handleCloseLinkedEntitiesModal = useCallback(() => {
    setLinkedEntitiesModalOpen(false);
  }, [setLinkedEntitiesModalOpen]);

  const handleSaveDocumentConfirmed = useCallback(() => {
    handleSaveDocument(saveAsModalAction);
    setLinkedEntitiesModalOpen(false);
  }, [handleSaveDocument, saveAsModalAction, setLinkedEntitiesModalOpen]);

  const renderToolbarContents = () => (
    <>
      <DiffSourceToggleWrapper options={['rich-text']}>
        <UndoRedo />
        <BlockTypeSelect />
        <BoldItalicUnderlineToggles />
        <ListsToggle />
        <InsertTable />
        <InsertThematicBreak />
        <CreateLink />
        <ConditionalContents
          options={[
            {
              when: (editor) => editor?.editorType === 'codeblock',
              contents: renderChangeCodeMirrorLanguage,
            },
            {
              fallback: renderInsertCodeBlock,
            },
          ]}
        />
      </DiffSourceToggleWrapper>

      {mode !== 'view' ? (
        <>
          <Box sx={{ display: 'none' }}>
            <FilterChipVersion
              placeholder="Search version"
              versionsList={versionList}
              getSelectedVersions={handleVersionsChange}
            />
          </Box>
          <IconButton
            disabled={!isUserAuthorizedForEditDocument}
            title={
              isUserAuthorizedForEditDocument
                ? 'Save document.'
                : NOT_AUTHORIZED_TO_ACCESS_MESSAGE
            }
            onClick={handleSaveClick}
          >
            <SaveIcon
              style={{
                width: '24px',
                height: '24px',
                color: theme?.palette?.primary?.main,
              }}
            />
          </IconButton>
          <IconButton
            disabled={!isUserAuthorizedForEditDocument}
            title={
              isUserAuthorizedForEditDocument
                ? 'Save as new version.'
                : 'Not authorized.'
            }
            onClick={handleSaveAsClick}
          >
            <SaveAsIcon
              style={{
                width: '24px',
                height: '24px',
                color: theme?.palette?.primary?.main,
              }}
            />
          </IconButton>
        </>
      ) : null}
    </>
  );

  const renderLargeFileWarning = () => {
    if (isLargeFile) {
      return (
        <Stack
          sx={{
            padding: '32px 16px',
            alignItems: 'center',
            gap: '32px',
          }}
        >
          <Typography
            sx={{
              textAlign: 'justify',
            }}
          >
            This file is too large to view or edit. Please download the file
            instead.
          </Typography>
        </Stack>
      );
    }
    return null;
  };

  return (
    <Box>
      {isLoading ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <CircularProgress color="secondary" />
        </Box>
      ) : (
        <Box>
          {markdown?.length && viewMode ? (
            <MDXEditor
              className={themeMode === 'dark' ? 'dark-theme' : 'light-theme'}
              ref={ref}
              key={viewMode}
              markdown={sanitizeMarkdown(markdown)}
              onChange={handleChange}
              suppressHtmlProcessing={false}
              plugins={[
                tablePlugin(),
                headingsPlugin(),
                thematicBreakPlugin(),
                listsPlugin(),
                linkPlugin(),
                linkDialogPlugin(),
                diffSourcePlugin({
                  diffMarkdown,
                  viewMode,
                }),
                codeBlockPlugin(),
                imagePlugin({ imageUploadHandler }),
                codeBlockPlugin({ defaultCodeBlockLanguage: 'js' }),
                codeMirrorPlugin({
                  codeBlockLanguages: {
                    js: 'JavaScript',
                    css: 'CSS',
                    html: 'HTML',
                  },
                }),
                mode !== 'view' &&
                  toolbarPlugin({
                    toolbarContents: renderToolbarContents,
                  }),
              ]}
              readOnly={mode === 'view'}
            />
          ) : null}
          {renderLargeFileWarning()}
        </Box>
      )}
      {mode !== 'view' && (
        <Dialog
          open={linkedEntitiesModalOpen}
          onClose={handleCloseLinkedEntitiesModal}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            Update Document
            <IconButton
              aria-label="close"
              onClick={handleCloseLinkedEntitiesModal}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            <DocumentLinkedEntities documentId={docDetails?.document_id} />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseLinkedEntitiesModal} variant="outlined">
              Close
            </Button>
            <Button
              onClick={handleSaveDocumentConfirmed}
              variant="contained"
              color="primary"
            >
              Update Document
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};
DocumentEditor.propTypes = {
  docDetails: PropTypes.oneOfType([PropTypes.object]).isRequired,
  getDocumentMarkdown: PropTypes.func.isRequired,
  getVersionDetails: PropTypes.func.isRequired,
  updateDocument: PropTypes.func.isRequired,
  saveAsNewVersion: PropTypes.func.isRequired,
  mode: PropTypes.oneOf(['edit', 'create']).isRequired,
  fetchDocumentsList: PropTypes.func.isRequired,
  entityId: PropTypes.number.isRequired,
  isUserAuthorizedForEditDocument: PropTypes.bool.isRequired,
};

export default DocumentEditor;
